/**
 * Network Status Service for InspectaOne
 *
 * Monitors network connectivity with real API verification
 * Provides network type detection and quality estimation
 *
 * @module lib/services/network-status
 */

'use client';

// ============================================================================
// Type Definitions
// ============================================================================

export type NetworkStatus = 'online' | 'offline' | 'slow';
export type NetworkType = '4g' | '3g' | '2g' | 'wifi' | 'ethernet' | 'unknown';

export interface NetworkInfo {
  isOnline: boolean;
  status: NetworkStatus;
  type: NetworkType;
  downlink?: number | undefined; // Mbps
  rtt?: number | undefined; // Round-trip time in ms
  effectiveType?: string | undefined; // From Network Information API
  saveData?: boolean | undefined; // User has enabled data saver mode
  lastChecked: string; // ISO timestamp
}

export type NetworkChangeListener = (info: NetworkInfo) => void;
export type ConnectivityRestoredListener = () => void;

// Extend the fallback type for NetworkInformation
interface NetworkInformationFallback {
  downlink?: number;
  effectiveType?: string;
  rtt?: number;
  saveData?: boolean;
  type?: string; // Add missing 'type' property
  addEventListener?: (type: string, listener: EventListenerOrEventListenerObject) => void;
  removeEventListener?: (type: string, listener: EventListenerOrEventListenerObject) => void; // Add missing method
}

type NetworkInformation = NetworkInformationFallback;

// ============================================================================
// Network Status Service Class
// ============================================================================

class NetworkStatusService {
  private listeners: Map<string, NetworkChangeListener> = new Map();
  private connectivityListeners: Set<ConnectivityRestoredListener> = new Set();
  private checkInterval: NodeJS.Timeout | null = null;
  private currentStatus: NetworkInfo;
  private readonly API_BASE = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000').replace(/\/$/, '');
  // Default to kong-exposed auth-service health route; can override via NEXT_PUBLIC_PING_PATH
  private readonly PING_PATH = process.env.NEXT_PUBLIC_PING_PATH || '/api/v1/auth/health';
  private readonly PING_FALLBACK_PATHS = ['/api/v1/health', '/health'];
  private readonly CHECK_INTERVAL_MS = 30000; // 30 seconds
  private readonly PING_TIMEOUT_MS = 5000; // 5 seconds
  private readonly SLOW_CONNECTION_THRESHOLD = 1; // Mbps
  private readonly HIGH_RTT_THRESHOLD = 500; // ms

  constructor() {
    this.currentStatus = {
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : false,
      status: typeof navigator !== 'undefined' && navigator.onLine ? 'online' : 'offline',
      type: 'unknown',
      lastChecked: new Date().toISOString(),
    };

    if (typeof window !== 'undefined') {
      this.initialize();
    }
  }

  /**
   * Initialize event listeners
   */
  private initialize(): void {
    // Basic online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);

    // Network Information API (if available)
    const connection = this.getConnection();
    if (connection) {
      connection.addEventListener?.('change', this.updateNetworkInfo); // Use optional chaining
      this.updateNetworkInfo();
    }

    // Initial connectivity check
    this.verifyConnectivity();

    // Start periodic checks
    this.startPeriodicCheck();
  }

  /**
   * Get the Network Information API connection object
   */
  private getConnection(): NetworkInformation | undefined {
    return (
      (navigator as Navigator & { connection?: NetworkInformation }).connection ||
      (navigator as Navigator & { mozConnection?: NetworkInformation }).mozConnection ||
      (navigator as Navigator & { webkitConnection?: NetworkInformation }).webkitConnection
    );
  }

  /**
   * Handle browser online event
   */
  private handleOnline = (): void => {
    console.log('[NetworkStatus] Browser detected online');
    this.currentStatus.isOnline = true;
    this.verifyConnectivity();
  };

  /**
   * Handle browser offline event
   */
  private handleOffline = (): void => {
    console.log('[NetworkStatus] Browser detected offline');
    this.currentStatus.isOnline = false;
    this.currentStatus.status = 'offline';
    this.currentStatus.lastChecked = new Date().toISOString();
    this.notifyListeners();
  };

  /**
   * Verify actual API connectivity by ping
   */
  private verifyConnectivity = async (): Promise<void> => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), this.PING_TIMEOUT_MS);

      console.log('[NetworkStatus] Verifying connectivity...');
      console.log('[NetworkStatus] navigator.onLine:', navigator.onLine);

      const pingUrls = [
        `${this.API_BASE}${this.PING_PATH.startsWith('/') ? '' : '/'}${this.PING_PATH}`,
        ...this.PING_FALLBACK_PATHS.map((path) => `${this.API_BASE}${path}`),
      ];

      let success = false;
      let lastStatus: number | undefined;

      for (const url of pingUrls) {
        try {
          const response = await fetch(url, { signal: controller.signal });
          lastStatus = response.status;
          if (response.ok) {
            console.log('[NetworkStatus] API ping successful', url);
            success = true;
            break;
          }
        } catch (err) {
          // Swallow and try next endpoint
          continue;
        }
      }

      clearTimeout(timeout);

      if (success) {
        this.currentStatus.isOnline = true;
        this.currentStatus.status = 'online';
      } else {
        console.warn('[NetworkStatus] API ping failed, last status:', lastStatus);
        this.currentStatus.isOnline = false;
        this.currentStatus.status = 'offline';
      }
    } catch (error) {
      console.error('[NetworkStatus] API ping error:', error);
      this.currentStatus.isOnline = false;
      this.currentStatus.status = 'offline';
    } finally {
      this.currentStatus.lastChecked = new Date().toISOString();
      this.notifyListeners();
    }
  };

  /**
   * Update network information from Network Information API
   */
  private updateNetworkInfo = (): void => {
    const connection = this.getConnection();
    if (!connection) return;

    // Update network type information
    this.currentStatus.downlink = connection.downlink;
    this.currentStatus.rtt = connection.rtt;
    this.currentStatus.effectiveType = connection.effectiveType;
    this.currentStatus.saveData = connection.saveData;
    this.currentStatus.lastChecked = new Date().toISOString();

    // Determine network type
    if (connection.type) {
      switch (connection.type) {
        case 'wifi':
          this.currentStatus.type = 'wifi';
          break;
        case 'ethernet':
          this.currentStatus.type = 'ethernet';
          break;
        case 'cellular':
        case 'wimax':
          // Use effectiveType for cellular
          this.determineEffectiveType(connection.effectiveType);
          break;
        default:
          this.currentStatus.type = 'unknown';
      }
    } else {
      // Fallback to effectiveType if type not available
      this.determineEffectiveType(connection.effectiveType);
    }

    // Determine if connection is slow
    if (this.currentStatus.isOnline) {
      const isSlowDownlink = connection.downlink && connection.downlink < this.SLOW_CONNECTION_THRESHOLD;
      const isHighRTT = connection.rtt && connection.rtt > this.HIGH_RTT_THRESHOLD;
      const isSlow2G = connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';

      if (isSlowDownlink || isHighRTT || isSlow2G) {
        this.currentStatus.status = 'slow';
      } else if (this.currentStatus.status === 'slow') {
        // Upgrade from slow to online if conditions improved
        this.currentStatus.status = 'online';
      }
    }

    this.notifyListeners();
  };

  /**
   * Determine network type from effective type
   */
  private determineEffectiveType(effectiveType?: string): void {
    if (!effectiveType) {
      this.currentStatus.type = 'unknown';
      return;
    }

    switch (effectiveType) {
      case '4g':
        this.currentStatus.type = '4g';
        break;
      case '3g':
        this.currentStatus.type = '3g';
        break;
      case '2g':
      case 'slow-2g':
        this.currentStatus.type = '2g';
        break;
      default:
        this.currentStatus.type = 'unknown';
    }
  }

  /**
   * Start periodic connectivity checks
   */
  private startPeriodicCheck(): void {
    this.checkInterval = setInterval(() => {
      if (this.currentStatus.isOnline) {
        this.verifyConnectivity();
      }
    }, this.CHECK_INTERVAL_MS);
  }

  /**
   * Stop periodic checks
   */
  private stopPeriodicCheck(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Notify all listeners of status change
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      listener(this.getStatus());
    });
  }

  // ============================================================================
  // Public API
  // ============================================================================

  /**
   * Get current network status
   */
  public getStatus(): NetworkInfo {
    return { ...this.currentStatus };
  }

  /**
   * Check if online
   */
  public isOnline(): boolean {
    return this.currentStatus.isOnline;
  }

  /**
   * Check if offline
   */
  public isOffline(): boolean {
    return !this.currentStatus.isOnline;
  }

  /**
   * Check if connection is slow
   */
  public isSlow(): boolean {
    return this.currentStatus.status === 'slow';
  }

  /**
   * Get network type
   */
  public getNetworkType(): NetworkType {
    return this.currentStatus.type;
  }

  /**
   * Get download speed (Mbps)
   */
  public getDownlinkSpeed(): number | undefined {
    return this.currentStatus.downlink;
  }

  /**
   * Get round-trip time (ms)
   */
  public getRTT(): number | undefined {
    return this.currentStatus.rtt;
  }

  /**
   * Check if user has data saver enabled
   */
  public isDataSaverEnabled(): boolean {
    return this.currentStatus.saveData || false;
  }

  /**
   * Force an immediate connectivity check
   */
  public async checkNow(): Promise<NetworkInfo> {
    await this.verifyConnectivity();
    return this.getStatus();
  }

  /**
   * Subscribe to network status changes
   */
  public subscribe(id: string, listener: NetworkChangeListener): void {
    this.listeners.set(id, listener);
  }

  /**
   * Unsubscribe from network status changes
   */
  public unsubscribe(id: string): void {
    this.listeners.delete(id);
  }

  /**
   * Subscribe to connectivity restored event
   */
  public onConnectivityRestored(listener: ConnectivityRestoredListener): void {
    this.connectivityListeners.add(listener);
  }

  /**
   * Unsubscribe from connectivity restored event
   */
  public offConnectivityRestored(listener: ConnectivityRestoredListener): void {
    this.connectivityListeners.delete(listener);
  }

  /**
   * Clean up and stop all monitoring
   */
  public destroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);

      const connection = this.getConnection();
      // Use optional chaining for removeEventListener
      if (connection) {
        connection.removeEventListener?.('change', this.updateNetworkInfo);
      }
    }

    this.stopPeriodicCheck();
    this.listeners.clear();
    this.connectivityListeners.clear();
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const networkStatusService = new NetworkStatusService();

// Clean up on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    networkStatusService.destroy();
  });
}
